import type { Modality } from '@/features/quotes/context/group-life-quote.context';

/**
 * Regras de capital por modalidade do plano PME (Vida em Grupo).
 *
 * Fonte: regras de negócio ALBA (2026-06). O piso individual varia por
 * modalidade. R$ 500.000 só entra como teto da vida quando a etapa do plano
 * ainda não definiu o Capital Máximo Individual.
 */

/** Teto de capital da vida quando o plano ainda não tem Capital Máximo Individual. */
export const GROUP_LIFE_CAPITAL_MAX = 500_000;

/**
 * Quantidades de salários aceitas na modalidade Múltiplo Salarial. São os
 * "Tipos de Fator Múltiplo" do i4Pro: a quantidade vai como `SalaryMultiplier`
 * para o cálculo e a emissão a traduz para o código do i4Pro (`fatorMultiplo`).
 * Valor fora desta lista barra a emissão.
 */
export const GROUP_LIFE_SALARY_MULTIPLIERS = [10, 12, 15, 20, 24] as const;

export function salaryMultiplierLabel(salaries: string | number): string {
  return `${salaries} x Salário Base Mensal`;
}

export function isAcceptedSalaryMultiplier(value: string | undefined): boolean {
  return GROUP_LIFE_SALARY_MULTIPLIERS.some(n => String(n) === value);
}

/** Piso do "Capital Mínimo Individual" por modalidade (0 = sem piso). */
export const GROUP_LIFE_MIN_INDIVIDUAL_CAPITAL: Record<Modality, number> = {
  UNIFORM: 0,
  VARIABLE: 5_000,
  MULTIPLE_SALARY: 5_000,
};

/**
 * Grupo segurável é obrigatório em todas as modalidades EXCETO Uniforme
 * (cadastro, edição e importação de vida).
 */
export function isInsurableGroupRequired(
  modality: Modality | undefined,
): boolean {
  return modality !== 'UNIFORM';
}

/**
 * Mapeia a DESCRIÇÃO de uma modalidade da DataTable `BenefitType` para o "kind"
 * interno que dirige o comportamento do step (campos/validações). A
 * classificação é por palavra-chave normalizada (sem acento, minúsculas).
 */
export function benefitKindFromDescription(
  desc: string | undefined,
): Modality | null {
  const n = (desc ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
  if (n.includes('uniforme')) return 'UNIFORM';
  if (n.includes('multiplo') || n.includes('salarial')) return 'MULTIPLE_SALARY';
  if (n.includes('variavel')) return 'VARIABLE';
  return null;
}
